import React, { useEffect, useState, useMemo, useRef } from "react";
import { CheckSquare, Filter, MessageSquare, Send, Paperclip, X, ExternalLink, Loader2, Clock, Users as UsersIcon, Search, Plus, Upload, Inbox, Layers, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/lib/auth.jsx";
import Layout, { PageHeader, StatCard } from "@/components/Layout";
import { getNav } from "@/pages/navConfigs";

/**
 * Admin-only "All Tasks" overview.
 *
 * - Lists every task across the org with filters (assigner / assignee / status / search).
 * - Admin can open any thread and post comments inline.
 * - Admin CANNOT toggle done/not-done. Status badge is read-only.
 */

function renderMentionsAdmin(text) {
  const parts = String(text || "").split(/(@[\p{L}0-9._-]+(?:\s[\p{L}0-9._-]+)?)/u);
  return parts.map((p, i) => {
    if (p.startsWith("@")) return <span key={i} className="bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded font-semibold">{p}</span>;
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

export default function AdminTasksPage() {
  const { user, customRole } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ assigned_by: "", assignee: "", status: "", q: "" });
  const [openTaskId, setOpenTaskId] = useState(null);
  const [showAssign, setShowAssign] = useState(false);
  const [editTaskId, setEditTaskId] = useState(null);
  const [view, setView] = useState("all"); // "all" | "to-me" | "by-me"

  const load = async () => {
    setLoading(true);
    try {
      const [t, u] = await Promise.all([api.get("/tasks"), api.get("/users").catch(() => ({ data: [] }))]);
      setTasks(t.data || []);
      setUsers(u.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const userById = (uid) => users.find((x) => x.user_id === uid);

  const scoped = useMemo(() => {
    if (view === "to-me") return tasks.filter((t) => (t.assignee_ids || []).includes(user.user_id));
    if (view === "by-me") return tasks.filter((t) => t.assigned_by === user.user_id);
    return tasks;
  }, [tasks, view, user.user_id]);

  const filtered = useMemo(() => {
    const s = (filters.q || "").toLowerCase().trim();
    return scoped.filter((t) => {
      const ids = t.assignee_ids || [];
      if (filters.assigned_by && t.assigned_by !== filters.assigned_by) return false;
      if (filters.assignee && !ids.includes(filters.assignee)) return false;
      if (filters.status) {
        const allDone = ids.length > 0 && ids.every((a) => (t.completions || {})[a]);
        const partDone = !allDone && Object.keys(t.completions || {}).length > 0;
        const status = allDone ? "done" : partDone ? "in_progress" : "pending";
        if (status !== filters.status) return false;
      }
      if (s) {
        const hay = `${t.title || ""} ${t.description || ""} ${t.assigned_by_name || ""}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [scoped, filters]);

  const viewCounts = useMemo(() => ({
    all: tasks.length,
    "to-me": tasks.filter((t) => (t.assignee_ids || []).includes(user.user_id)).length,
    "by-me": tasks.filter((t) => t.assigned_by === user.user_id).length,
  }), [tasks, user.user_id]);

  const totals = useMemo(() => {
    let done = 0, prog = 0, pend = 0;
    for (const t of scoped) {
      const ids = t.assignee_ids || [];
      const completions = t.completions || {};
      const allDone = ids.length > 0 && ids.every((a) => completions[a]);
      const partDone = !allDone && Object.keys(completions).length > 0;
      if (allDone) done++;
      else if (partDone) prog++;
      else pend++;
    }
    return { total: scoped.length, done, prog, pend };
  }, [scoped]);

  const openTask = tasks.find((t) => t.task_id === openTaskId);
  const editTask = tasks.find((t) => t.task_id === editTaskId);

  const removeTask = async (t) => {
    if (!window.confirm(`Delete task "${t.title}"? This also removes its thread and messages.`)) return;
    try {
      await api.delete(`/tasks/${t.task_id}`);
      toast.success("Task deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <Layout navItems={getNav(user.role, customRole)}>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto">
        <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
          <PageHeader
            title="All Tasks (Admin)"
            subtitle="Every task across the org. Filter, open threads, comment — assignees mark completion. Admin can also assign new tasks."
            icon={CheckSquare}
            accent="orange"
          />
          <button
            onClick={() => setShowAssign(true)}
            className="btn-primary px-4 py-2.5 shrink-0"
            data-testid="admin-assign-task-btn"
          >
            <Plus className="w-4 h-4" /> Assign Task
          </button>
        </div>

        <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="admin-tasks-tabs">
          {[
            { id: "all", label: "All Tasks", Icon: Layers, count: viewCounts.all },
            { id: "to-me", label: "Assigned to me", Icon: Inbox, count: viewCounts["to-me"] },
            { id: "by-me", label: "Assigned by me", Icon: Send, count: viewCounts["by-me"] },
          ].map((tab) => {
            const active = view === tab.id;
            const Icon = tab.Icon;
            return (
              <button
                key={tab.id}
                onClick={() => setView(tab.id)}
                className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition-all ${
                  active ? "brand-gradient text-white shadow" : "bg-white border border-slate-200 text-slate-600 hover:border-orange-200"
                }`}
                data-testid={`admin-tasks-tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" /> {tab.label}
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-white/25" : "bg-slate-100 text-slate-500"}`}>{tab.count}</span>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <StatCard label="Total" value={totals.total} icon={CheckSquare} color="indigo" />
          <StatCard label="Pending" value={totals.pend} icon={Clock} color="amber" />
          <StatCard label="In progress" value={totals.prog} icon={UsersIcon} color="sky" />
          <StatCard label="Done" value={totals.done} icon={CheckSquare} color="emerald" />
        </div>

        <div className="dashboard-card p-4 mb-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                className="input-pill text-sm w-full" style={{ paddingLeft: "2.5rem" }}
                placeholder="Search title / description…"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                data-testid="admin-tasks-search"
              />
            </div>
            <select className="input-pill text-sm" value={filters.assigned_by} onChange={(e) => setFilters({ ...filters, assigned_by: e.target.value })} data-testid="admin-tasks-filter-assigner">
              <option value="">Any assigner</option>
              {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name} · {ROLE_LABELS[u.role] || u.role}</option>)}
            </select>
            <select className="input-pill text-sm" value={filters.assignee} onChange={(e) => setFilters({ ...filters, assignee: e.target.value })} data-testid="admin-tasks-filter-assignee">
              <option value="">Any assignee</option>
              {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name} · {ROLE_LABELS[u.role] || u.role}</option>)}
            </select>
            <select className="input-pill text-sm" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="admin-tasks-filter-status">
              <option value="">Any status</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In progress</option>
              <option value="done">Done</option>
            </select>
          </div>
          <div className="text-xs text-slate-400 mt-2">{filtered.length} of {scoped.length} task{scoped.length === 1 ? "" : "s"} {view === "to-me" && "assigned to you"}{view === "by-me" && "assigned by you"}</div>
        </div>

        <div className="dashboard-card p-6">
          {loading && <div className="text-center py-8 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}
          {!loading && filtered.length === 0 && <div className="text-sm text-slate-400 text-center py-8">No tasks match the filters.</div>}
          <div className="space-y-3">
            {filtered.map((t) => {
              const ids = t.assignee_ids || [];
              const completions = t.completions || {};
              const allDone = ids.length > 0 && ids.every((a) => completions[a]);
              const partDone = !allDone && Object.keys(completions).length > 0;
              const status = allDone ? "Done" : partDone ? "In progress" : "Pending";
              const statusColor = allDone ? "bg-emerald-100 text-emerald-700 border-emerald-200" : partDone ? "bg-sky-100 text-sky-700 border-sky-200" : "bg-amber-100 text-amber-700 border-amber-200";
              return (
                <div key={t.task_id} className={`p-4 rounded-xl border ${allDone ? "bg-emerald-50/40 border-emerald-200" : "border-slate-100"}`} data-testid={`admin-task-${t.task_id}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <div className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${statusColor}`}>{status}</div>
                        <div className={`font-semibold truncate ${allDone ? "line-through text-slate-400" : ""}`}>{t.title}</div>
                      </div>
                      <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1"><Send className="w-3 h-3" /> from <b className="text-slate-700">{t.assigned_by_name || "—"}</b></span>
                        <span className="inline-flex items-center gap-1"><UsersIcon className="w-3 h-3" /> {ids.length} assignee{ids.length === 1 ? "" : "s"}</span>
                        {t.deadline && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> due {t.deadline}</span>}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {ids.map((uid) => {
                          const a = userById(uid);
                          const done = !!completions[uid];
                          return (
                            <span key={uid} className={`text-[10px] px-2 py-0.5 rounded-full ${done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`} title={done ? "Completed" : "Not yet"}>
                              {done && "✓ "}{a?.name || uid.slice(0, 8)} <span className="opacity-60">· {ROLE_LABELS[a?.role] || a?.role || ""}</span>
                            </span>
                          );
                        })}
                      </div>
                      {t.description && <div className="text-sm text-slate-600 mt-2 line-clamp-3">{t.description}</div>}
                      {t.attachment_url && <a href={t.attachment_url} target="_blank" rel="noreferrer" className="text-xs text-orange-600 underline mt-1 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Initial attachment</a>}
                      <div className="mt-2 flex items-center gap-3 flex-wrap">
                        <button
                          onClick={() => setOpenTaskId(t.task_id)}
                          className="text-xs text-orange-600 hover:underline inline-flex items-center gap-1"
                          data-testid={`admin-task-open-thread-${t.task_id}`}
                        >
                          <MessageSquare className="w-3 h-3" /> Open thread & comment
                        </button>
                        <button
                          onClick={() => setEditTaskId(t.task_id)}
                          className="text-xs text-sky-600 hover:underline inline-flex items-center gap-1"
                          data-testid={`admin-task-edit-${t.task_id}`}
                        >
                          <Pencil className="w-3 h-3" /> Edit
                        </button>
                        <button
                          onClick={() => removeTask(t)}
                          className="text-xs text-rose-600 hover:underline inline-flex items-center gap-1"
                          data-testid={`admin-task-delete-${t.task_id}`}
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {openTask && <AdminTaskThread task={openTask} users={users} onClose={() => setOpenTaskId(null)} />}
      {showAssign && <AdminAssignTaskModal users={users.filter((u) => u.user_id !== user.user_id && u.active !== false)} onClose={() => setShowAssign(false)} onSaved={() => { setShowAssign(false); load(); }} />}
      {editTask && <AdminEditTaskModal task={editTask} users={users.filter((u) => u.active !== false)} onClose={() => setEditTaskId(null)} onSaved={() => { setEditTaskId(null); load(); }} />}
    </Layout>
  );
}

function AdminAssignTaskModal({ users, onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", assignee_ids: [], deadline: "", attachment_url: "" });
  const [saving, setSaving] = useState(false);
  const [roleFilter, setRoleFilter] = useState("");
  const [search, setSearch] = useState("");

  const roles = useMemo(() => Array.from(new Set(users.map((u) => u.role))).sort(), [users]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    return users
      .filter((u) => (roleFilter ? u.role === roleFilter : true))
      .filter((u) => (s ? `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(s) : true))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [users, search, roleFilter]);

  const toggle = (uid) => setForm((f) => ({
    ...f,
    assignee_ids: f.assignee_ids.includes(uid) ? f.assignee_ids.filter((x) => x !== uid) : [...f.assignee_ids, uid],
  }));

  const uploadAttach = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/upload", fd);
      setForm({ ...form, attachment_url: `${BACKEND_URL}${r.data.url}` });
      toast.success("Attached");
    } catch { toast.error("Upload failed"); }
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    if (form.assignee_ids.length === 0) return toast.error("Pick at least 1 assignee");
    setSaving(true);
    try {
      await api.post("/tasks", form);
      toast.success(`Task assigned to ${form.assignee_ids.length} member${form.assignee_ids.length === 1 ? "" : "s"}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="admin-assign-task-modal">
        <div className="p-5 border-b border-slate-100 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase font-bold text-orange-500 mb-1">Admin · Assign task</div>
            <h3 className="font-display font-black text-xl">Send work to any member</h3>
            <p className="text-xs text-slate-500 mt-1">Admin can assign to any active user across every role.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-100" data-testid="admin-assign-task-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <input
            className="input-pill"
            placeholder="Task title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="admin-assign-title"
          />
          <textarea
            rows={3}
            className="input-pill resize-none"
            placeholder="Description / instructions"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="admin-assign-description"
          />

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Deadline (optional — 24h reminder email)</label>
              <input
                type="datetime-local"
                className="input-pill"
                value={form.deadline}
                onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                data-testid="admin-assign-deadline"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Attachment (optional)</label>
              <label className="btn-ghost py-2.5 text-sm cursor-pointer w-full justify-center">
                <Upload className="w-3 h-3" /> {form.attachment_url ? "Replace file" : "Attach file"}
                <input type="file" className="hidden" onChange={(e) => uploadAttach(e.target.files?.[0])} data-testid="admin-assign-attach" />
              </label>
              {form.attachment_url && <div className="text-[11px] text-emerald-600 mt-1">✓ Attachment ready</div>}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-slate-600">Assign to (pick 1+ members)</label>
              <div className="text-[11px] text-emerald-600 font-bold">{form.assignee_ids.length} selected</div>
            </div>
            <div className="flex gap-2 mb-2">
              <div className="relative flex-1">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  className="input-pill text-sm pl-9"
                  placeholder="Search name / email / role"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="admin-assign-search"
                />
              </div>
              <select
                className="input-pill text-sm w-48"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                data-testid="admin-assign-role-filter"
              >
                <option value="">All roles</option>
                {roles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
              </select>
            </div>
            <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-xl p-2 space-y-1 bg-slate-50/40" data-testid="admin-assign-user-list">
              {filtered.length === 0 && <div className="text-xs text-slate-400 p-3 text-center">No matching members</div>}
              {filtered.map((u) => {
                const checked = form.assignee_ids.includes(u.user_id);
                return (
                  <label
                    key={u.user_id}
                    className={`flex items-center gap-2 px-2 py-2 rounded cursor-pointer ${checked ? "bg-orange-100" : "hover:bg-white"}`}
                    data-testid={`admin-assign-opt-${u.user_id}`}
                  >
                    <input type="checkbox" checked={checked} onChange={() => toggle(u.user_id)} className="rounded" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-800 truncate">{u.name}</div>
                      <div className="text-[10px] text-slate-500 truncate">{u.email}</div>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 shrink-0">{ROLE_LABELS[u.role] || u.role}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button
            onClick={submit}
            disabled={saving || !form.title.trim() || form.assignee_ids.length === 0}
            className="btn-primary px-5 disabled:opacity-50"
            data-testid="admin-assign-submit"
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Assigning…</> : <><Send className="w-4 h-4" /> Assign task</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminTaskThread({ task, users, onClose }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState([]);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  // @-mention typeahead — restricted to actual participants of THIS task
  // (assignees + creator), which is how mention notifications resolve on
  // the backend. Prevents spamming random users via mention.
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [caret, setCaret] = useState(0);

  const participantUsers = useMemo(() => {
    const ids = new Set([...(task.assignee_ids || []), task.assigned_by].filter(Boolean));
    return users.filter((u) => ids.has(u.user_id));
  }, [task, users]);

  const mentionOptions = useMemo(() => {
    const q = (mentionQuery || "").toLowerCase();
    return participantUsers.filter((u) => (u.name || "").toLowerCase().includes(q)).slice(0, 8);
  }, [participantUsers, mentionQuery]);

  const onKeyOrChange = (e) => {
    const el = e.target;
    const val = el.value;
    setText(val);
    setCaret(el.selectionStart || 0);
    const upto = val.slice(0, el.selectionStart || 0);
    const m = upto.match(/@([\p{L}0-9._-]{0,30})$/u);
    if (m) { setMentionQuery(m[1]); setShowMentions(true); } else { setShowMentions(false); }
  };

  const insertMention = (u) => {
    const upto = text.slice(0, caret);
    const rest = text.slice(caret);
    const replaced = upto.replace(/@([\p{L}0-9._-]{0,30})$/u, `@${(u.name || "").replace(/\s+/g, " ")} `);
    setText(replaced + rest);
    setShowMentions(false);
    setTimeout(() => { inputRef.current?.focus(); }, 50);
  };

  const load = async () => {
    const r = await api.get(`/tasks/${task.task_id}/messages`);
    setMsgs(r.data || []);
  };
  useEffect(() => { load(); }, [task.task_id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/upload", fd);
      setPending((p) => [...p, { url: `${BACKEND_URL}${r.data.url}`, name: file.name, kind: file.type }]);
    } catch { toast.error("Upload failed"); }
  };

  const send = async () => {
    if (!text.trim() && pending.length === 0) return;
    setSending(true);
    try {
      await api.post(`/tasks/${task.task_id}/messages`, { text: text.trim(), attachments: pending });
      setText(""); setPending([]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    finally { setSending(false); }
  };

  const userById = (uid) => users.find((x) => x.user_id === uid);
  const assignees = task.assignee_ids || [];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="admin-task-thread-modal">
        <div className="p-5 border-b border-slate-100 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase font-bold text-slate-400 mb-1">Admin view · Comment-only</div>
            <h3 className="font-display font-black text-xl break-words">{task.title}</h3>
            <div className="text-xs text-slate-500 mt-1">From <b>{task.assigned_by_name}</b> · {assignees.length} assignee{assignees.length === 1 ? "" : "s"}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-100" data-testid="admin-task-thread-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 bg-slate-50 border-b border-slate-100 text-xs">
          <div className="flex flex-wrap gap-1.5">
            {assignees.map((uid) => {
              const u = userById(uid);
              const done = !!(task.completions || {})[uid];
              return (
                <span key={uid} className={`px-2 py-0.5 rounded-full ${done ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                  {done && "✓ "}{u?.name || uid.slice(0, 8)}
                </span>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-slate-50/40">
          {msgs.length === 0 && <div className="text-center text-sm text-slate-400 py-6">No messages yet — start the thread.</div>}
          {msgs.map((m) => {
            const mine = m.sender_id === user.user_id;
            return (
              <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl p-3 ${mine ? "bg-slate-200 text-slate-800" : "bg-white border border-slate-200"}`} data-testid={`admin-task-msg-${m.message_id}`}>
                  <div className={`text-[10px] font-bold mb-1 ${mine ? "text-slate-500" : "text-slate-500"}`}>
                    {m.sender_name} · {ROLE_LABELS[m.sender_role] || m.sender_role}
                  </div>
                  {m.text && <div className="text-sm whitespace-pre-wrap break-words">{renderMentionsAdmin(m.text)}</div>}
                  {Array.isArray(m.attachments) && m.attachments.map((a, i) => (
                    <a key={i} href={a.url} target="_blank" rel="noreferrer" className={`text-xs underline inline-flex items-center gap-1 mt-1 ${mine ? "text-orange-100" : "text-orange-600"}`}>
                      <Paperclip className="w-3 h-3" /> {a.name || "attachment"}
                    </a>
                  ))}
                  <div className={`text-[10px] mt-1 ${mine ? "text-orange-100" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleString()}</div>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>

        <div className="p-4 border-t border-slate-100">
          {pending.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {pending.map((p, i) => (
                <span key={i} className="bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                  {p.name} <button onClick={() => setPending((prev) => prev.filter((_, j) => j !== i))}><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <label className="cursor-pointer p-2 rounded-full hover:bg-slate-100" title="Attach a file">
              <Paperclip className="w-4 h-4 text-slate-500" />
              <input type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
            </label>
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                rows={2}
                className="input-pill w-full text-sm resize-none"
                placeholder="Comment as Admin… Type @ to tag a teammate (they'll be emailed)"
                value={text}
                onChange={onKeyOrChange}
                onKeyUp={onKeyOrChange}
                onClick={onKeyOrChange}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !showMentions) { e.preventDefault(); send(); } }}
                data-testid="admin-task-msg-input"
              />
              {showMentions && mentionOptions.length > 0 && (
                <div className="absolute bottom-full mb-1 left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-lg z-30 max-h-56 overflow-y-auto" data-testid="admin-task-mention-dropdown">
                  {mentionOptions.map((u) => (
                    <button
                      key={u.user_id}
                      type="button"
                      onClick={() => insertMention(u)}
                      className="w-full text-left px-3 py-2 hover:bg-orange-50 text-sm flex items-center gap-2"
                      data-testid={`admin-mention-opt-${u.user_id}`}
                    >
                      <div className="w-6 h-6 rounded-full bg-orange-100 grid place-items-center text-[10px] font-bold text-orange-700">{(u.name || "?").slice(0, 2).toUpperCase()}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-800 text-sm truncate">{u.name}</div>
                        <div className="text-[10px] text-slate-500">{ROLE_LABELS[u.role] || u.role}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={send} disabled={sending || (!text.trim() && pending.length === 0)} className="btn-primary px-4 py-2 disabled:opacity-50" data-testid="admin-task-msg-send">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function AdminEditTaskModal({ task, users, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: task.title || "",
    description: task.description || "",
    assignee_ids: [...(task.assignee_ids || [])],
    deadline: task.deadline || "",
    attachment_url: task.attachment_url || "",
  });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const roles = useMemo(() => Array.from(new Set(users.map((u) => u.role))).sort(), [users]);

  const filteredUsers = useMemo(() => {
    const s = search.toLowerCase().trim();
    return users
      .filter((u) => (roleFilter ? u.role === roleFilter : true))
      .filter((u) => (s ? `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(s) : true))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [users, search, roleFilter]);

  const toggle = (uid) => setForm((f) => ({
    ...f,
    assignee_ids: f.assignee_ids.includes(uid) ? f.assignee_ids.filter((x) => x !== uid) : [...f.assignee_ids, uid],
  }));

  const uploadAttach = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/upload", fd);
      setForm({ ...form, attachment_url: `${BACKEND_URL}${r.data.url}` });
      toast.success("Attached");
    } catch { toast.error("Upload failed"); }
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    if (form.assignee_ids.length === 0) return toast.error("Pick at least 1 assignee");
    setSaving(true);
    try {
      await api.patch(`/tasks/${task.task_id}`, form);
      toast.success("Task updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="admin-edit-task-modal">
        <div className="p-5 border-b border-slate-100 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase font-bold text-sky-600 mb-1">Admin · Edit task</div>
            <h3 className="font-display font-black text-xl">Update task details</h3>
            <p className="text-xs text-slate-500 mt-1">Change title, description, deadline, attachment, or reassign to different members.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-100" data-testid="admin-edit-task-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <input
            className="input-pill"
            placeholder="Task title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="admin-edit-title"
          />
          <textarea
            rows={3}
            className="input-pill resize-none"
            placeholder="Description / instructions"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="admin-edit-description"
          />

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Deadline</label>
              <input
                type="datetime-local"
                className="input-pill"
                value={form.deadline || ""}
                onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                data-testid="admin-edit-deadline"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Attachment</label>
              <label className="btn-ghost py-2.5 text-sm cursor-pointer w-full justify-center">
                <Upload className="w-3 h-3" /> {form.attachment_url ? "Replace file" : "Attach file"}
                <input type="file" className="hidden" onChange={(e) => uploadAttach(e.target.files?.[0])} data-testid="admin-edit-attach" />
              </label>
              {form.attachment_url && (
                <div className="text-[11px] text-emerald-600 mt-1 truncate">
                  ✓ <a href={form.attachment_url} target="_blank" rel="noreferrer" className="underline">Attachment</a> ·{" "}
                  <button type="button" onClick={() => setForm({ ...form, attachment_url: "" })} className="text-rose-500 hover:underline">remove</button>
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-slate-600">Reassign to (pick 1+ members)</label>
              <div className="text-[11px] text-emerald-600 font-bold">{form.assignee_ids.length} selected</div>
            </div>
            <div className="flex gap-2 mb-2">
              <div className="relative flex-1">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  className="input-pill text-sm pl-9"
                  placeholder="Search name / email / role"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="admin-edit-search"
                />
              </div>
              <select
                className="input-pill text-sm w-48"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                data-testid="admin-edit-role-filter"
              >
                <option value="">All roles</option>
                {roles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
              </select>
            </div>
            <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-xl p-2 space-y-1 bg-slate-50/40" data-testid="admin-edit-user-list">
              {filteredUsers.length === 0 && <div className="text-xs text-slate-400 p-3 text-center">No matching members</div>}
              {filteredUsers.map((u) => {
                const checked = form.assignee_ids.includes(u.user_id);
                return (
                  <label
                    key={u.user_id}
                    className={`flex items-center gap-2 px-2 py-2 rounded cursor-pointer ${checked ? "bg-sky-100" : "hover:bg-white"}`}
                    data-testid={`admin-edit-opt-${u.user_id}`}
                  >
                    <input type="checkbox" checked={checked} onChange={() => toggle(u.user_id)} className="rounded" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-800 truncate">{u.name}</div>
                      <div className="text-[10px] text-slate-500 truncate">{u.email}</div>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 shrink-0">{ROLE_LABELS[u.role] || u.role}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button
            onClick={submit}
            disabled={saving || !form.title.trim() || form.assignee_ids.length === 0}
            className="btn-primary px-5 disabled:opacity-50"
            data-testid="admin-edit-submit"
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Pencil className="w-4 h-4" /> Save changes</>}
          </button>
        </div>
      </div>
    </div>
  );
}
